import React, { useState } from 'react';
import { Persona } from '../../types/persona';
import { updatePersona } from '../../services/api';

interface PersonaEditorProps {
  persona: Persona;
  onSave: () => void;
  onCancel: () => void;
}

export const PersonaEditor: React.FC<PersonaEditorProps> = ({ persona, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Persona>(persona);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await updatePersona(persona.personaId, {
        id: formData.id,
        name: formData.name,
        demographics: formData.demographics,
        cognitive: formData.cognitive,
        psychographics: formData.psychographics,
        brand_relationship: formData.brand_relationship,
        professional_context: formData.professional_context,
        narrative_bio: formData.narrative_bio,
      });
      onSave();
    } catch (err) {
      console.error('Error updating persona:', err);
      setError(err instanceof Error ? err.message : 'Failed to update persona');
      setSaving(false);
    }
  };

  const updateField = (path: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const parts = path.split('.');
      let current: any = newData;

      for (let i = 0; i < parts.length - 1; i++) {
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = value;
      return newData;
    });
  };

  const addValue = () => {
    setFormData((prev) => ({
      ...prev,
      psychographics: {
        ...prev.psychographics,
        values: [...prev.psychographics.values, ''],
      },
    }));
  };

  const removeValue = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      psychographics: {
        ...prev.psychographics,
        values: prev.psychographics.values.filter((_, i) => i !== index),
      },
    }));
  };

  const updateValue = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      psychographics: {
        ...prev.psychographics,
        values: prev.psychographics.values.map((v, i) => (i === index ? value : v)),
      },
    }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="bg-primary-500 px-6 py-4">
              <h2 className="text-2xl font-bold text-white">Edit Persona</h2>
            </div>

            {/* Content */}
            <div className="px-6 py-6 max-h-[calc(100vh-12rem)] overflow-y-auto space-y-6">
              {/* Error Message */}
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-800">{error}</div>
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Demographics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.demographics.age}
                      onChange={(e) => updateField('demographics.age', parseInt(e.target.value))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.demographics.gender}
                      onChange={(e) => updateField('demographics.gender', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.demographics.location}
                      onChange={(e) => updateField('demographics.location', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Occupation
                    </label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.demographics.occupation}
                      onChange={(e) => updateField('demographics.occupation', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Income</label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.demographics.income}
                      onChange={(e) => updateField('demographics.income', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Family Status
                    </label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.demographics.family_status}
                      onChange={(e) => updateField('demographics.family_status', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Cognitive */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Cognitive Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IQ (0-200)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="200"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.cognitive.iq}
                      onChange={(e) => updateField('cognitive.iq', parseInt(e.target.value))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Critical Thinking (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.cognitive.critical_thinking}
                      onChange={(e) =>
                        updateField('cognitive.critical_thinking', parseInt(e.target.value))
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specific Knowledge (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.cognitive.knowledge_specific}
                      onChange={(e) =>
                        updateField('cognitive.knowledge_specific', parseInt(e.target.value))
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category Knowledge (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.cognitive.knowledge_category}
                      onChange={(e) =>
                        updateField('cognitive.knowledge_category', parseInt(e.target.value))
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Media Consumption
                    </label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.cognitive.media_consumption}
                      onChange={(e) => updateField('cognitive.media_consumption', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Political Alignment
                    </label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.cognitive.political_alignment}
                      onChange={(e) => updateField('cognitive.political_alignment', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Psychographics */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Psychographics</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Values</label>
                    {formData.psychographics.values.map((value, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          className="block flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={value}
                          onChange={(e) => updateValue(index, e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => removeValue(index)}
                          className="px-3 py-2 border border-gray-300 rounded-md text-red-600 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addValue}
                      className="mt-2 px-3 py-2 border border-gray-300 rounded-md text-primary-500 hover:bg-primary-50 text-sm"
                    >
                      Add Value
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Personality
                    </label>
                    <textarea
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.psychographics.personality}
                      onChange={(e) => updateField('psychographics.personality', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Goals</label>
                    <textarea
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.psychographics.goals}
                      onChange={(e) => updateField('psychographics.goals', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Brand Relationship */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Brand Relationship</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usage Status
                    </label>
                    <input
                      type="text"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.brand_relationship.usage_status}
                      onChange={(e) =>
                        updateField('brand_relationship.usage_status', e.target.value)
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Perception
                    </label>
                    <textarea
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.brand_relationship.perception}
                      onChange={(e) => updateField('brand_relationship.perception', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pain Points
                    </label>
                    <textarea
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      value={formData.brand_relationship.pain_points}
                      onChange={(e) =>
                        updateField('brand_relationship.pain_points', e.target.value)
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Narrative Bio */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Narrative Biography</h3>
                <textarea
                  rows={6}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.narrative_bio}
                  onChange={(e) => updateField('narrative_bio', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
